import {
  useFloating,
  useDismiss,
  useInteractions,
  offset,
  flip,
  autoUpdate,
} from '@floating-ui/react';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { faCalendar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'classnames';
import { format, parse, isValid as isValidDate } from 'date-fns';
import FocusTrap from 'focus-trap-react';
import { range } from 'lodash';
import React, { useEffect, useState } from 'react';
import type { CalendarMonth } from 'react-day-picker';
import { DayPicker, useDayPicker } from 'react-day-picker';
import { useWatch } from 'react-hook-form';
import MaskedInput from 'react-text-mask';
import { FormGroup, Label, Button, InputGroup } from 'reactstrap';

import { Tooltip } from '../../_components/tooltip';
import { blue } from '../../app/colors';
import { useThemeContext } from '../../app/theme-provider';
import SingleErrorDisplay from '../error-display/single-error-display';
import { useFormFieldContext } from '../form-hook';
import { useToastr } from '../toastr';

import { Explanation } from './explanation-modal';
import styles from './index.module.scss';
import './overrides.scss';

export interface DateFieldProps {
  id?: string;
  name: string;
  label: string;
  required: boolean;
  formGroup?: any;
  dataTestid?: string;
  maxDate?: Date;
  disabled?: boolean;
  minDate?: Date;
  information: string;
}

export function DateFieldInfo(props: DateFieldProps) {
  const {
    name,
    required,
    label,
    formGroup,
    dataTestid,
    maxDate,
    disabled,
    minDate,
    information,
  } = props;
  const toast = useToastr();
  const value = useWatch({ name }) || '';
  const { theme } = useThemeContext();
  const { isNotValid, setValue: setFormFieldValue } = useFormFieldContext(name);
  const dateFormat = 'MM/dd/yyyy';
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [month, setMonth] = useState(value || new Date());
  const [inputValue, setInputValue] = useState<string | undefined>();

  const yearMask = [/\d/, /\d/, /\d/, /\d/];
  const monthMask = [/\d/, /\d/];
  const dayMask = [/\d/, /\d/];
  const target = `text-mask-${name}`;
  /* eslint-disable react-hooks/refs */
  // eslint identifies false positive for the 'refs' returned
  // by useFloating (https://github.com/floating-ui/floating-ui/discussions/3405)
  // If react ref objects are ever used in this file, refine
  // when it is turned off and on to protect the use of those refs
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [offset(10), flip()],
  });
  const dismiss = useDismiss(context, {
    enabled: true,
    escapeKey: false,
    outsidePress: () => {
      setIsOpen(false);
      return true;
    },
  });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    if (value) setMonth(value);
  }, [value]);

  useEffect(() => {
    const escape = (event: any) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [setIsOpen]);

  const setValue = (val?: Date) => {
    setInputValue(val ? format(val, dateFormat) : '');
    setFormFieldValue(val, { shouldDirty: true, shouldValidate: true });
  };

  const disabledDates = () => {
    if (!maxDate && !minDate) {
      return [];
    }
    if (maxDate) {
      return [{ after: maxDate }];
    }
    if (minDate) {
      return [{ before: minDate }];
    }
  };

  return (
    <FormGroup {...formGroup}>
      {label ? (
        <>
          <Label
            className={cx('me-2', required && 'required', value && 'stay')}
            for={target}
          >
            {label}
          </Label>
          <Tooltip tooltip={'Click for more information'}>
            <button
              onClick={(e) => {
                e.preventDefault();
                setIsModalOpen(!isModalOpen);
              }}
              className="btn btn-transparent m-0 p-0"
            >
              <FontAwesomeIcon
                icon={faInfoCircle}
                size="lg"
                color={theme === 'dark' ? '#52baff' : blue}
              />
            </button>
          </Tooltip>
        </>
      ) : null}
      <Explanation
        titleLabel="Date Of Service"
        information={information}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
      />
      <div className="label-target" style={{ position: 'relative' }}>
        <div ref={refs.setReference}>
          <InputGroup>
            <MaskedInput
              disabled={disabled}
              id={target}
              autoComplete="off"
              data-testid={dataTestid ?? name}
              mask={[...monthMask, '/', ...dayMask, '/', ...yearMask]}
              className={cx(
                { [styles.isInvalid]: isNotValid },
                styles.input,
                'form-control'
              )}
              guide={false}
              onChange={(event: any) => {
                const text = event.target.value;
                if (/^\d\d\/\d\d\/\d\d\d\d$/.test(text)) {
                  const parsedDate = parse(text, dateFormat, new Date());
                  if (isValidDate(parsedDate)) {
                    setValue(parsedDate);
                  }
                } else {
                  setInputValue(text);
                  setFormFieldValue(undefined, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              placeholder="mm/dd/yyyy"
              defaultValue={value ? format(value, dateFormat) : undefined}
              value={inputValue}
              {...getReferenceProps()}
              aria-label={`${label}. ${required ? 'Required.' : ''}`}
            />
            <Button
              disabled={disabled}
              className={cx(
                'px-1',
                {
                  [styles.isInvalid]: isNotValid,
                },
                styles.calendarButton
              )}
              onClick={() => setIsOpen((s) => !s)}
              aria-label="Open Calendar"
              aria-description={
                value
                  ? `Change Date, ${format(value, 'PPPP')}.`
                  : 'Select date.'
              }
            >
              <FontAwesomeIcon
                className={styles.calendarIcon}
                icon={faCalendar}
              />
            </Button>
          </InputGroup>
        </div>
        {isOpen && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cx(styles.popoverContainer, 'border border-dark')}
            {...getFloatingProps()}
          >
            <FocusTrap>
              <div>
                <DayPicker
                  mode="single"
                  autoFocus
                  onSelect={(date) => {
                    if (date) {
                      toast.announce(`Value selected. ${format(date, 'PPPP')}`);
                    }
                    setValue(date);
                    setIsOpen(false);
                  }}
                  selected={value}
                  month={month}
                  onMonthChange={setMonth}
                  hideNavigation
                  disabled={disabledDates()}
                  components={{
                    MonthCaption: NavigationHeader,
                  }}
                />
                <div className={`d-flex py-1 ${styles.footer}`}>
                  <Button
                    aria-label="Close calendar"
                    size="sm"
                    color="primary"
                    className={`closeButton ms-auto m-2 ${styles.closeButton}`}
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </FocusTrap>
          </div>
        )}
      </div>
      <SingleErrorDisplay field={name} />
    </FormGroup>
  );
}

function NavigationHeader({ calendarMonth }: { calendarMonth: CalendarMonth }) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  const year = calendarMonth.date.getFullYear();
  const month = calendarMonth.date.getMonth();

  const onMonthChange = (event: any) => {
    const newDate = new Date(calendarMonth.date);
    newDate.setMonth(Number.parseInt(event?.target.value));
    goToMonth(newDate);
  };

  const onYearChange = (event: any) => {
    const newDate = new Date(calendarMonth.date);
    newDate.setFullYear(Number.parseInt(event?.target.value));
    goToMonth(newDate);
  };

  return (
    <div
      className={cx(
        styles.navHeader,
        'border-bottom border-dark align-items-center'
      )}
    >
      <div>
        <Button
          color="primary"
          size="sm"
          onClick={() => previousMonth && goToMonth(previousMonth)}
          title="Previous Month"
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </Button>
      </div>
      <div>
        <select onChange={onMonthChange} value={month} aria-label="Month">
          <option value="0">January</option>
          <option value="1">February</option>
          <option value="2">March</option>
          <option value="3">April</option>
          <option value="4">May</option>
          <option value="5">June</option>
          <option value="6">July</option>
          <option value="7">August</option>
          <option value="8">September</option>
          <option value="9">October</option>
          <option value="10">November</option>
          <option value="11">December</option>
        </select>
      </div>
      <div>
        <select onChange={onYearChange} value={year} aria-label="Year">
          {range(year - 100, year + 100).map((year, i) => (
            <option key={i} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Button
          size="sm"
          color="primary"
          onClick={() => nextMonth && goToMonth(nextMonth)}
          title="Next Month"
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </Button>
      </div>
    </div>
  );
}
